import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  ArrowLeft, 
  Search
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  collection, 
  getDocs, 
  deleteDoc, 
  doc,
  query,
  orderBy 
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import toast from 'react-hot-toast';
import AddFoodItemForm from '../../components/admin/AddFoodItemForm';
import FoodTypeBadge from '../../components/FoodTypeBadge';
import CategoryManagement from '../../components/admin/CategoryManagement';

const MenuManagementNewFixed = () => {
  const navigate = useNavigate();
  
  const [food_items, setFoodItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showCategoryManagement, setShowCategoryManagement] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12); // Limit items per page

  useEffect(() => {
    fetchFoodItems();
  }, []);

  const fetchFoodItems = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching food items from Firestore...');
      const q = query(collection(db, 'food_items'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('📦 Fetched items:', items.length, 'items');
      console.log('📋 Items data:', items);
      setFoodItems(items);
    } catch (error) {
      console.error('❌ Error fetching food items:', error);
      toast.error('Failed to fetch food items');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowAddForm(true);
  };

  const handleDelete = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this food item?')) {
      try {
        await deleteDoc(doc(db, 'food_items', itemId));
        toast.success('Food item deleted successfully!');
        fetchFoodItems();
      } catch (error) {
        console.error('Error deleting food item:', error);
        toast.error('Failed to delete food item');
      }
    }
  };

  // Optimized filtering with useMemo
  const filteredItems = useMemo(() => {
    console.log('🔍 Filtering items:', {
      totalItems: food_items.length,
      searchQuery,
      selectedCategory
    });
    const filtered = food_items.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           item.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
    console.log('✅ Filtered result:', filtered.length, 'items');
    return filtered;
  }, [food_items, searchQuery, selectedCategory]);

  // Pagination logic
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);

  // Reset to first page when search or category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  const FoodItemCard = React.memo(({ item }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s ease'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <img
          src={item.imageUrl || '/images/placeholder-main-course.svg'}
          alt={item.name}
          style={{
            width: '80px',
            height: '80px',
            objectFit: 'cover',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
          onError={(e) => {
            e.target.src = '/images/placeholder-main-course.svg';
          }}
        />
        <div style={{ flexGrow: 1 }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937',
            margin: '0 0 8px 0'
          }}>
            {item.name}
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 8px 0',
            lineHeight: '1.4'
          }}>
            {item.description}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#059669'
            }}>
              ₹{item.price?.half || 0} / ₹{item.price?.full || 0}
            </span>
            <span style={{
              fontSize: '12px',
              color: '#6b7280',
              textTransform: 'capitalize',
              backgroundColor: '#f3f4f6',
              padding: '4px 8px',
              borderRadius: '6px'
            }}>
              {item.category}
            </span>
            <FoodTypeBadge foodType={item.foodType || 'veg'} size="small" />
            {item.isPopular && (
              <span style={{
                fontSize: '12px',
                color: '#dc2626',
                backgroundColor: '#fee2e2',
                padding: '4px 8px',
                borderRadius: '6px',
                fontWeight: '600'
              }}>
                ⭐ Popular
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => handleEdit(item)}
            style={{
              padding: '8px',
              backgroundColor: '#f0f9ff',
              color: '#0369a1',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f0f9ff'}
          >
            <Edit style={{ width: '16px', height: '16px' }} />
          </button>
          <button
            onClick={() => handleDelete(item.id)}
            style={{
              padding: '8px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fecaca'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
          >
            <Trash2 style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      </div>
    </div>
  ));

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #ff6b35',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ fontSize: '16px', color: '#6b7280' }}>Loading menu items...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        padding: '16px 0',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => navigate('/admin')}
                style={{
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowLeft style={{ width: '20px', height: '20px' }} />
              </button>
              <div style={{ width: '48px', height: '48px', background: 'linear-gradient(45deg, #ff6b35, #ff3f3f)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(255,107,53,0.3)' }}>
                <span style={{ color: 'white', fontWeight: '900', fontSize: '1.5rem' }}>B</span>
              </div>
              <div>
                <h1 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1f2937', margin: 0 }}>Menu Management</h1>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>Manage your food items</p>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <Button
                onClick={() => setShowCategoryManagement(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6366f1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4f46e5'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6366f1'}
              >
                <Plus style={{ width: '20px', height: '20px' }} />
                Manage Categories
              </Button>
              <Button
                onClick={() => {
                  setEditingItem(null);
                  setShowAddForm(true);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ff6b35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(255,107,53,0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e55a2b'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ff6b35'}
              >
                <Plus style={{ width: '20px', height: '20px' }} />
                Add Food Item
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Search Bar */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ position: 'relative' }}>
            <Search style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              width: '20px',
              height: '20px'
            }} />
            <Input
              type="text"
              placeholder="Search food items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '12px 12px 12px 48px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                width: '100%',
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>
        </div>

        {/* Category Filter */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>
              Filter by Category:
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'white',
                transition: 'border-color 0.2s ease',
                minWidth: '200px'
              }}
              onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            >
              <option value="all">All Categories</option>
              <option value="main-course">Main Course</option>
              <option value="appetizers">Appetizers</option>
              <option value="desserts">Desserts</option>
              <option value="beverages">Beverages</option>
              <option value="soups">Soups</option>
              <option value="salads">Salads</option>
              <option value="breads">Breads</option>
              <option value="rice">Rice</option>
              <option value="curries">Curries</option>
              <option value="snacks">Snacks</option>
              <option value="sweets">Sweets</option>
            </select>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              Showing {filteredItems.length} of {food_items.length} items
            </div>
          </div>
        </div>

        {/* Food Items Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '20px'
        }}>
          {filteredItems.length === 0 ? (
            <div style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, #ffe4e6, #ffc0cb)',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px auto',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}>
                <span style={{ fontSize: '48px' }}>🍽️</span>
              </div>
              <h3 style={{
                fontSize: '1.8rem',
                fontWeight: 'bold',
                color: '#1f2937',
                margin: '0 0 12px 0'
              }}>
                {searchQuery ? 'No items found' : 'No food items yet'}
              </h3>
              <p style={{
                color: '#6b7280',
                fontSize: '1.1rem',
                margin: '0 0 24px 0'
              }}>
                {searchQuery ? 'Try adjusting your search' : 'Add your first food item to get started'}
              </p>
              {!searchQuery && (
                <Button
                  onClick={() => setShowAddForm(true)}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto',
                    boxShadow: '0 4px 12px rgba(255,107,53,0.3)'
                  }}
                >
                  <Plus style={{ width: '20px', height: '20px' }} />
                  Add First Item
                </Button>
              )}
            </div>
          ) : (
            <>
              {currentItems.map((item) => (
                <FoodItemCard key={item.id} item={item} />
              ))}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  gridColumn: '1 / -1',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px',
                  marginTop: '32px',
                  padding: '20px',
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: currentPage === 1 ? '#f3f4f6' : '#ff6b35',
                      color: currentPage === 1 ? '#9ca3af' : 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Previous
                  </button>
                  
                  <span style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#ff6b35',
                      color: currentPage === totalPages ? '#9ca3af' : 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Add Food Item Form */}
      {showAddForm && (
        <AddFoodItemForm 
          onClose={() => {
            console.log('🚪 Closing add form...');
            setShowAddForm(false);
            setEditingItem(null);
          }}
          onSuccess={() => {
            console.log('✅ Form submission successful, refreshing data...');
            fetchFoodItems();
          }}
          editingItem={editingItem}
        />
      )}

      {/* Category Management */}
      {showCategoryManagement && (
        <CategoryManagement 
          onClose={() => {
            console.log('🚪 Closing category management...');
            setShowCategoryManagement(false);
          }}
        />
      )}
    </div>
  );
};

export default MenuManagementNewFixed;
